const DB_NAME = 'lineup-auth';
const DB_VERSION = 1;
const KEY_STORE = 'keys';
const KEY_ID = 'remembered-credentials-key';
const CREDENTIALS_STORAGE_KEY = 'lineup.rememberedCredentials';

interface RememberedCredentials {
  identifier: string;
  password: string;
}

interface EncryptedCredentials {
  iv: string;
  data: string;
}

const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary);
};

const base64ToArrayBuffer = (value: string): ArrayBuffer => {
  const binary = window.atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
};

const openDatabase = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
  const request = window.indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    if (!request.result.objectStoreNames.contains(KEY_STORE)) {
      request.result.createObjectStore(KEY_STORE);
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const readKey = async (database: IDBDatabase): Promise<CryptoKey | undefined> =>
  new Promise((resolve, reject) => {
    const transaction = database.transaction(KEY_STORE, 'readonly');
    const request = transaction.objectStore(KEY_STORE).get(KEY_ID);
    request.onsuccess = () => resolve(request.result as CryptoKey | undefined);
    request.onerror = () => reject(request.error);
  });

const writeKey = async (database: IDBDatabase, key: CryptoKey): Promise<void> =>
  new Promise((resolve, reject) => {
    const transaction = database.transaction(KEY_STORE, 'readwrite');
    transaction.objectStore(KEY_STORE).put(key, KEY_ID);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });

const getOrCreateEncryptionKey = async (): Promise<CryptoKey> => {
  const database = await openDatabase();
  try {
    const existingKey = await readKey(database);
    if (existingKey) return existingKey;
    const key = await window.crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
    await writeKey(database, key);
    return key;
  } finally {
    database.close();
  }
};

export const saveRememberedCredentials = async (
  credentials: RememberedCredentials
): Promise<void> => {
  if (!window.crypto?.subtle || !window.indexedDB) {
    throw new Error('当前环境不支持安全保存登录凭据');
  }
  const key = await getOrCreateEncryptionKey();
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(credentials));
  const encrypted = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
  const payload: EncryptedCredentials = {
    iv: bytesToBase64(iv),
    data: bytesToBase64(new Uint8Array(encrypted)),
  };
  window.localStorage.setItem(CREDENTIALS_STORAGE_KEY, JSON.stringify(payload));
};

export const loadRememberedCredentials = async (): Promise<RememberedCredentials | null> => {
  const raw = window.localStorage.getItem(CREDENTIALS_STORAGE_KEY);
  if (!raw || !window.crypto?.subtle || !window.indexedDB) return null;
  try {
    const payload = JSON.parse(raw) as EncryptedCredentials;
    const key = await getOrCreateEncryptionKey();
    const decrypted = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: base64ToArrayBuffer(payload.iv) },
      key,
      base64ToArrayBuffer(payload.data)
    );
    const credentials = JSON.parse(new TextDecoder().decode(decrypted)) as RememberedCredentials;
    return credentials.identifier && credentials.password ? credentials : null;
  } catch (error) {
    console.warn('读取已保存的登录凭据失败，将清除失效数据:', error);
    window.localStorage.removeItem(CREDENTIALS_STORAGE_KEY);
    return null;
  }
};

export const clearRememberedCredentials = (): void => {
  window.localStorage.removeItem(CREDENTIALS_STORAGE_KEY);
};
